import type { AxiosInstance } from 'axios';

import type {
  FilterCharacter,
  FilterFandom,
  FilterRatings,
  FilterStatuses,
  FilterTag,
  FilterUniverse,
} from '@/types/filter-data';
import type { CatalogFilterLockedFacets, CatalogFilterPageKind } from '../types';
import {
  buildCatalogFilterFetchPlan,
  type CatalogFilterFetchMode,
} from './build-catalog-filter-fetch-plan.util';
import { resolveNestedFandomId } from './resolve-nested-fandom-id.util';
import { resolvePermanentUrl } from './resolve-permanent-url';

export interface FanficsFilterOptions {
  initialFandoms: FilterFandom[];
  initialStatuses: FilterStatuses | undefined;
  initialRatings: FilterRatings | undefined;
  initialTags: FilterTag[];
  initialUniverses: FilterUniverse[];
  initialCharacters: FilterCharacter[];
}

interface FetchFanficsFilterOptionsParams {
  serverApi: AxiosInstance;
  type: 'fanfic' | 'original';
  pageKind?: CatalogFilterPageKind;
  fetchMode?: CatalogFilterFetchMode;
  lockedFacets?: CatalogFilterLockedFacets;
  // Character IDs currently selected (e.g. restored from a permalink pageKey)
  selectedCharacterIds?: string[];
  // Tag IDs currently selected (e.g. restored from a permalink pageKey or
  // legacy ?tags=id GET param)
  selectedTagIds?: string[];
  signal?: AbortSignal;
}

interface FandomCustomItem {
  id: number;
  title?: string;
  sec_title?: string;
  slug?: string | null;
  fanfic_cnt?: number;
  permanentUrl?: string | null;
}

interface TagCustomItem {
  id: number;
  title?: string;
  isAdult?: boolean;
  is_adult?: boolean;
  usageCount?: number;
  permanentUrl?: string | null;
}

interface UniverseCustomItem {
  title?: string;
  sec_title?: string;
  fanfic_cnt?: number;
}

interface CharacterCustomItem {
  id: number;
  name?: string;
  fanfic_cnt?: number;
  fandom_title?: string;
  permanentUrl?: string | null;
  fandomId?: number | string;
  fandom_id?: number | string;
  fandom?: { id?: number | string } | number | string | null;
}

interface StatusCustomItem {
  status: string;
  count: number;
}

interface RatingCustomItem {
  rating: string;
  count: number;
}

const EMPTY_STATUSES: FilterStatuses = {
  completed: 0,
  'in-progress': 0,
  freezed: 0,
};

const EMPTY_RATINGS: FilterRatings = {
  '0': 0,
  '6': 0,
  '12': 0,
  '16': 0,
  '18': 0,
};

const EMPTY_RESPONSE = { data: {} };

const appendWorkLevelLocks = (
  params: URLSearchParams,
  lockedFacets: CatalogFilterLockedFacets,
): void => {
  if (lockedFacets.fandomId) params.set('fandoms', lockedFacets.fandomId);
  if (lockedFacets.universeId) params.set('universes', lockedFacets.universeId);
  if (lockedFacets.characterIds?.length) {
    params.set('characters', lockedFacets.characterIds.join(','));
  }
  if (lockedFacets.pairingCharacters?.length) {
    params.set('pairingCharacters', lockedFacets.pairingCharacters.join(','));
  }
  if (lockedFacets.tagIds?.length) params.set('tags', lockedFacets.tagIds.join(','));
};

const buildFandomsQuery = (
  type: 'fanfic' | 'original',
  strategy: 'generic' | 'locked',
  lockedFacets: CatalogFilterLockedFacets,
): string => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('limit', '50');
  params.set('minFanfics', '1');
  params.set('type', type);
  if (strategy === 'locked') appendWorkLevelLocks(params, lockedFacets);
  return params.toString();
};

const buildTagsQuery = (
  type: 'fanfic' | 'original',
  strategy: 'generic' | 'locked',
  lockedFacets: CatalogFilterLockedFacets,
): string => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('limit', '100');
  params.set('type', type);
  if (strategy === 'locked') appendWorkLevelLocks(params, lockedFacets);
  return params.toString();
};

const buildUniversesQuery = (type: 'fanfic' | 'original'): string => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('limit', '50');
  params.set('type', type);
  return params.toString();
};

const buildStatusOrRatingQuery = (
  type: 'fanfic' | 'original',
  strategy: 'generic' | 'locked',
  lockedFacets: CatalogFilterLockedFacets,
): string => {
  const params = new URLSearchParams();
  params.set('type', type);
  if (strategy === 'locked') appendWorkLevelLocks(params, lockedFacets);
  return params.toString();
};

const buildCharactersQuery = (
  type: 'fanfic' | 'original',
  strategy: 'generic' | 'fandomEntity' | 'universeEntity' | 'tagsOnly',
  lockedFacets: CatalogFilterLockedFacets,
): string | null => {
  const params = new URLSearchParams();
  params.set('limit', '20');
  params.set('type', type);

  // Prefer work-level `fandom=` (same as fanfics-custom/v2) over entity
  // `fandomId`, which includes works whose fandoms array is empty and inflates
  // fanfic_cnt relative to the listing grid.
  if (strategy === 'fandomEntity') {
    if (!lockedFacets.fandomId) {
      // Refuse unscoped fetch — bare characters-custom returns the global top list.
      return null;
    }
    params.set('fandom', lockedFacets.fandomId);
  } else if (strategy === 'generic' && lockedFacets.fandomId) {
    params.set('fandom', lockedFacets.fandomId);
  }
  if (strategy === 'universeEntity' && lockedFacets.universeId) {
    params.set('universeId', lockedFacets.universeId);
  }
  if (strategy === 'tagsOnly' && lockedFacets.tagIds?.length) {
    params.set('tags', lockedFacets.tagIds.join(','));
  }

  return params.toString();
};

const mapFandoms = (fandoms: FandomCustomItem[] | undefined): FilterFandom[] =>
  (fandoms ?? []).map((fandom) => ({
    id: fandom.id.toString(),
    name: fandom.title || fandom.sec_title || '',
    count: fandom.fanfic_cnt || 0,
    slug: fandom.slug?.trim() || undefined,
    // Only trust API permanentUrl as SEO-enabled signal. Do not synthesize a
    // /fanfics/fandoms/{slug} href from catalog slug — disabled SEO pages must
    // render as plain text in filters (CheckboxItem falls back to <span>).
    permanentUrl: resolvePermanentUrl(fandom.permanentUrl),
  }));

const mapTags = (tags: TagCustomItem[] | undefined): FilterTag[] =>
  (tags ?? []).map((tag) => ({
    id: tag.id.toString(),
    name: `${tag.title ?? ''}${tag.isAdult || tag.is_adult ? ' 18+' : ''}`.trim(),
    count: tag.usageCount || 0,
    permanentUrl: resolvePermanentUrl(tag.permanentUrl),
  }));

const mapUniverses = (universes: UniverseCustomItem[] | undefined): FilterUniverse[] =>
  (universes ?? []).map((universe) => ({
    name: universe.sec_title || universe.title || '',
    count: universe.fanfic_cnt || 0,
  }));

const mapCharacters = (characters: CharacterCustomItem[] | undefined): FilterCharacter[] =>
  (characters ?? []).map((character) => ({
    id: character.id.toString(),
    name: character.name || '',
    count: character.fanfic_cnt || 0,
    fandomTitle: character.fandom_title || '',
    permanentUrl: resolvePermanentUrl(character.permanentUrl),
    fandomId: resolveNestedFandomId(character),
  }));

const mapStatuses = (items: StatusCustomItem[] | undefined): FilterStatuses | undefined => {
  if (!items) return undefined;
  return items.reduce(
    (accumulator: FilterStatuses, current) => {
      if (
        current.status === 'completed' ||
        current.status === 'in-progress' ||
        current.status === 'freezed'
      ) {
        accumulator[current.status] = current.count;
      }
      return accumulator;
    },
    { ...EMPTY_STATUSES },
  );
};

const mapRatings = (items: RatingCustomItem[] | undefined): FilterRatings | undefined => {
  if (!items) return undefined;
  return items.reduce(
    (accumulator: FilterRatings, current) => {
      if (['0', '6', '12', '16', '18'].includes(current.rating)) {
        accumulator[current.rating as keyof FilterRatings] = current.count;
      }
      return accumulator;
    },
    { ...EMPTY_RATINGS },
  );
};

const mergeUniqueById = <Item extends { id: string }>(
  primary: Item[],
  secondary: Item[],
): Item[] => {
  const merged = [...primary];
  for (const item of secondary) {
    if (!merged.some((existing) => existing.id === item.id)) {
      merged.push(item);
    }
  }
  return merged;
};

/**
 * characters-custom / tags-custom ignore Payload-style `where[id][in]=…`
 * (it returns the default top list). Work-level `characters=` / `tags=` filters
 * do include the requested entities so we can resolve selected ids to names
 * when deep-linking via GET (`/fanfics?characters=427287`).
 */
const buildSelectedCharactersQuery = (
  characterIds: string[],
  type: 'fanfic' | 'original',
  lockedFacets: CatalogFilterLockedFacets = {},
): string => {
  const params = new URLSearchParams();
  params.set('characters', characterIds.join(','));
  params.set('type', type);
  params.set('limit', String(Math.max(20, characterIds.length * 20)));
  // Keep selected-character fanfic_cnt aligned with the scoped list / grid
  if (lockedFacets.fandomId) {
    params.set('fandom', lockedFacets.fandomId);
  }
  return params.toString();
};

const buildSelectedTagsQuery = (tagIds: string[], type: 'fanfic' | 'original'): string => {
  const params = new URLSearchParams();
  params.set('tags', tagIds.join(','));
  params.set('type', type);
  params.set('limit', String(Math.max(20, tagIds.length * 20)));
  return params.toString();
};

/**
 * Fetches the aggregate filter option lists (fandoms, tags, universes, statuses,
 * ratings, characters) that FanfictionFilters renders as checkboxes. Shared by
 * listing pages (app/fanfics/page.tsx, app/originals/page.tsx) and permalink SSR
 * pages (fandoms/[slug], tags/[tagSlug], characters/[characterSlug], pairings/[pairingSlug]).
 *
 * On permalink pages the dictionaries are scoped to the page's locked facet
 */
export const fetchFanficsFilterOptions = async ({
  serverApi,
  type,
  pageKind = 'catalog',
  fetchMode = 'ssr',
  lockedFacets = {},
  selectedCharacterIds = [],
  selectedTagIds = [],
  signal,
}: FetchFanficsFilterOptionsParams): Promise<FanficsFilterOptions> => {
  const fetchPlan = buildCatalogFilterFetchPlan(pageKind, fetchMode, type);
  const requestConfig = signal ? { signal } : undefined;

  // Create all promises in parallel for optimal performance
  // These are intentionally not awaited individually - they will be awaited together via Promise.allSettled
  const promises = [
    // Fandoms
    fetchPlan.fandoms === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : serverApi.get(
          `/api/fandoms-custom?${buildFandomsQuery(type, fetchPlan.fandoms, lockedFacets)}`,
          requestConfig,
        ),
    // Statuses
    fetchPlan.statuses === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : serverApi.get(
          `/api/post-status-custom?${buildStatusOrRatingQuery(type, fetchPlan.statuses, lockedFacets)}`,
          requestConfig,
        ),
    // Ratings
    fetchPlan.ratings === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : serverApi.get(
          `/api/rating-custom?${buildStatusOrRatingQuery(type, fetchPlan.ratings, lockedFacets)}`,
          requestConfig,
        ),
    // Tags
    fetchPlan.tags === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : serverApi.get(
          `/api/tags-custom?${buildTagsQuery(type, fetchPlan.tags, lockedFacets)}`,
          requestConfig,
        ),
    // Universes
    fetchPlan.universes === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : serverApi.get(`/api/universes-custom?${buildUniversesQuery(type)}`, requestConfig),
    // Characters
    fetchPlan.characters === 'skip'
      ? Promise.resolve(EMPTY_RESPONSE)
      : (() => {
          const charactersQuery = buildCharactersQuery(type, fetchPlan.characters, lockedFacets);
          if (charactersQuery == null) {
            return Promise.resolve({ data: { characters: [] } });
          }
          return serverApi.get(`/api/characters-custom?${charactersQuery}`, requestConfig);
        })(),
    // Specific characters (selected) — skip when the characters facet is not rebuilt
    fetchPlan.characters !== 'skip' && selectedCharacterIds.length > 0
      ? serverApi.get(
          `/api/characters-custom?${buildSelectedCharactersQuery(selectedCharacterIds, type, lockedFacets)}`,
          requestConfig,
        )
      : Promise.resolve({ data: { characters: [] } }),
    // Specific tags (selected) — skip when the tags facet is not rebuilt
    fetchPlan.tags !== 'skip' && selectedTagIds.length > 0
      ? serverApi.get(
          `/api/tags-custom?${buildSelectedTagsQuery(selectedTagIds, type)}`,
          requestConfig,
        )
      : Promise.resolve({ data: { tags: [] } }),
  ] as const;

  const [
    fandomsResponse,
    statusesResponse,
    ratingsResponse,
    tagsResponse,
    universesResponse,
    charactersResponse,
    specificCharactersResponse,
    specificTagsResponse,
  ] = await Promise.allSettled(promises);

  const initialFandoms: FilterFandom[] =
    fetchPlan.fandoms === 'skip' || fandomsResponse.status !== 'fulfilled'
      ? []
      : mapFandoms(fandomsResponse.value.data.fandoms);

  const initialStatuses: FilterStatuses | undefined =
    fetchPlan.statuses === 'skip' || statusesResponse.status !== 'fulfilled'
      ? undefined
      : mapStatuses(statusesResponse.value.data.data);

  const initialRatings: FilterRatings | undefined =
    fetchPlan.ratings === 'skip' || ratingsResponse.status !== 'fulfilled'
      ? undefined
      : mapRatings(ratingsResponse.value.data.data);

  const topTags: FilterTag[] =
    fetchPlan.tags === 'skip' || tagsResponse.status !== 'fulfilled'
      ? []
      : mapTags(tagsResponse.value.data.tags);

  const selectedTagIdSet = new Set(selectedTagIds);
  const specificTags: FilterTag[] =
    specificTagsResponse.status === 'fulfilled'
      ? mapTags(specificTagsResponse.value.data.tags).filter((tag) => selectedTagIdSet.has(tag.id))
      : [];

  const initialTags = mergeUniqueById(specificTags, topTags);

  const initialUniverses: FilterUniverse[] =
    fetchPlan.universes === 'skip' || universesResponse.status !== 'fulfilled'
      ? []
      : mapUniverses(universesResponse.value.data.universes);

  const topCharacters: FilterCharacter[] =
    fetchPlan.characters === 'skip' || charactersResponse.status !== 'fulfilled'
      ? []
      : mapCharacters(charactersResponse.value.data.characters);

  const selectedCharacterIdSet = new Set(selectedCharacterIds);
  const specificCharacters: FilterCharacter[] =
    specificCharactersResponse.status === 'fulfilled'
      ? mapCharacters(specificCharactersResponse.value.data.characters).filter((character) =>
          selectedCharacterIdSet.has(character.id),
        )
      : [];

  const initialCharacters = mergeUniqueById(specificCharacters, topCharacters);

  return {
    initialFandoms,
    initialStatuses,
    initialRatings,
    initialTags,
    initialUniverses,
    initialCharacters,
  };
};
